import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import Badge, { BadgeVariant } from 'components/Badge'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import Loader from 'components/Loader'
import { AutoRow, RowBetween } from 'components/Row'
import Slider from 'components/Slider'
import { StepRequired } from 'components/StepLine/common/Step'
import { useStepLineNext } from 'components/StepLine/context/hooks/useStepLineNext'
import { useStepLineStateUpdate } from 'components/StepLine/context/StepLineContext'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { useToken, useTokens } from 'hooks/Tokens'
import { useCalculationEarnings, useKodiakFarmApr } from 'hooks/useKodiakFarm'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useContext, useEffect, useMemo } from 'react'
import useLockTimeValue, { useLockTimeMultiplier } from 'state/farms/hooks/useLockTimeValue'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'
import { KodiakFarm } from 'types/farms'
import { Abi } from 'viem'
import { useReadContract } from 'wagmi'

export const useSelectLockTimeStep = (farm: KodiakFarm | null | undefined, value?: string): StepRequired => {
  const { lockTime, setLockTime } = useLockTimeValue(
    parseInt(farm?.lockTimeMin.toString() || '0'),
    parseInt(farm?.lockTimeMax.toString() || '0')
  )
  const { condition, next, back } = useStepLineNext('selectLockTimeStepDone')

  return {
    title: 'Select Lock Time',
    condition,
    onBack: back,
    component: (
      <SelectLockTimeStep
        setLockTime={setLockTime}
        typedValue={value}
        lockTime={lockTime}
        farm={farm || undefined}
        next={next}
      />
    ),
  }
}

export type SelectLockTimeStepProps = {
  lockTime: number
  farm?: KodiakFarm
  setLockTime: (value: number) => void
  next: () => void
  typedValue: string | undefined
}

export type SelectLockTimeState = {
  lockTime: number
  apr?: number | null
  multiplier?: number | null
  earnings: (CurrencyAmount<Token> | undefined)[]
  rewardTokens: (Token | null | undefined)[]
}

const SelectLockTimeStep = ({ lockTime, setLockTime, typedValue, farm, next }: SelectLockTimeStepProps) => {
  const multiplier = useLockTimeMultiplier(lockTime, farm)
  const stakingToken = useToken(farm?.stakingToken.id)
  const amount = useMemo(
    () => tryParseCurrencyAmount<Token>(typedValue, stakingToken || undefined),
    [typedValue, stakingToken]
  )

  // const apr = useV2FarmApr(farm, multiplier, amount)
  const apr = useKodiakFarmApr(farm, multiplier, amount)
  const theme = useContext(ThemeContext)
  const earnings = useCalculationEarnings({
    lockTimeValue: lockTime,
    multiplier,
    farm,
    inputValue: typedValue || '1',
  })

  const { data: rewardTokensResponse } = useReadContract({
    address: farm?.address,
    abi: KODIAK_FARM_ABI as Abi,
    functionName: 'getAllRewardTokens',
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!farm?.address,
    },
  })
  const rewardTokens = useTokens(rewardTokensResponse as string[])

  const update = useStepLineStateUpdate<SelectLockTimeState>()
  useEffect(() => {
    update({ lockTime, apr, multiplier, rewardTokens, earnings })
  }, [lockTime, apr, multiplier, rewardTokens, earnings, update])

  return (
    <Column gap="8px" width="100%">
      <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>Select lock-up period</ThemedText.DeprecatedSubHeader>
      {lockTime / (3600 * 24)} days
      <Slider
        step={3600 * 24}
        min={parseInt(farm?.lockTimeMin.toString() || '0')}
        max={parseInt(farm?.lockTimeMax.toString() || '0')}
        value={lockTime}
        onChange={setLockTime}
      ></Slider>
      <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
        Estimated award by the end of the lock-up period
      </ThemedText.DeprecatedSubHeader>
      <LightCard>
        <AutoColumn gap="sm">
          {rewardTokens.map((rewardToken, index) => {
            return (
              <RowBetween key={index}>
                <AutoRow gap="2px">
                  <CurrencyLogo currency={rewardToken} size="20px" />
                  <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
                    {rewardToken?.symbol}
                  </ThemedText.DeprecatedSubHeader>
                </AutoRow>
                <AutoRow justify="flex-end">
                  <ThemedText.DeprecatedSubHeader color={theme.textPrimary}>
                    {earnings?.[index]?.toSignificant(2)}{' '}
                    {lockTime === 0 ? (
                      <Badge size="sm" variant={BadgeVariant.WARNING}>
                        per year
                      </Badge>
                    ) : (
                      <Badge size="sm" variant={BadgeVariant.PRIMARY}>
                        per {lockTime / (3600 * 24)} days
                      </Badge>
                    )}{' '}
                  </ThemedText.DeprecatedSubHeader>
                </AutoRow>
              </RowBetween>
            )
          })}

          <Separator />
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>Stake amount</ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              {amount?.toSignificant(6)} {stakingToken?.symbol}
            </ThemedText.DeprecatedSubHeader>
          </RowBetween>
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>Multiplier</ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              x{multiplier.toFixed(2)}
            </ThemedText.DeprecatedSubHeader>
          </RowBetween>
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>APR</ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              {typeof apr === 'number' ? (
                apr > 1000000 ? (
                  '>1,000,000%'
                ) : apr < 0.01 ? (
                  '<0.01%'
                ) : (
                  `${apr.toFixed(2)}%`
                )
              ) : (
                <Loader />
              )}
            </ThemedText.DeprecatedSubHeader>
          </RowBetween>
        </AutoColumn>
      </LightCard>
      <ButtonPrimary onClick={next}>Confirm</ButtonPrimary>
    </Column>
  )
}

export default SelectLockTimeStep
